import { normalizePath } from '@/util/normalizePath';

/** The URL fields analytics.js fills in itself, from `window.location`. */
const PAGE_URL_KEYS = ['url', 'path', 'referrer', 'search'] as const;

type PageFields = Record<string, unknown>;

/**
 * Masks a URL the same way `path_template` is masked, so the two cannot
 * disagree. Another site's URL is returned unchanged — an external referrer is
 * the one attribution signal here that a mask would destroy.
 *
 * @param raw - An absolute or root-relative URL
 * @returns The origin plus the masked pathname, with no query string
 */
export const maskSameOrigin = (raw: string): string => {
  let url: URL;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return '';
  }
  if (url.origin !== window.location.origin) {
    return raw;
  }
  return url.origin + normalizePath(url.pathname);
};

/** Rewrites the URL fields of one object, in place. */
export const sanitizePageFields = (fields?: PageFields) => {
  if (!fields) {
    return;
  }
  for (const key of PAGE_URL_KEYS) {
    const value = fields[key];
    if (typeof value !== 'string' || value === '') {
      continue;
    }
    if (key === 'search') {
      fields[key] = '';
    } else if (key === 'path') {
      fields[key] = normalizePath(value);
    } else {
      fields[key] = maskSameOrigin(value);
    }
  }
};

type Payload = { properties?: PageFields; context?: { page?: PageFields } };

/**
 * Masks both copies of the URL: `properties` on a page call, `context.page` on
 * every call. Mutates, because the payload is the transport.
 *
 * A console URL carries an end-customer id on the campaign subscriber journey,
 * and an SSO token in `?token=` on the partner iframe flow. No call site writes
 * these properties, so no call site can keep either out.
 */
export const sanitizePayload = (payload: Payload) => {
  sanitizePageFields(payload.properties);
  sanitizePageFields(payload.context?.page);
  return payload;
};

/**
 * Installs the mask on the Segment client, in every environment. Register it
 * before the validator, so the validator judges what actually leaves.
 *
 * @param analytics - The Segment client; a client with no middleware support is ignored
 */
export const registerPathSanitizer = (analytics: unknown) => {
  const host = analytics as {
    addSourceMiddleware?: (
      fn: (ctx: { payload: { obj: Payload }; next: (p: unknown) => void }) => void
    ) => unknown;
  };

  if (typeof host.addSourceMiddleware !== 'function') {
    return;
  }

  host.addSourceMiddleware(({ payload, next }) => {
    try {
      sanitizePayload(payload?.obj ?? {});
    } catch (error) {
      console.error('[analytics] the url mask itself failed', error);
    }
    // On every path: a middleware that throws drops all telemetry, silently.
    next(payload);
  });
};
